import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Info,
  X,
  AlertTriangle,
  CheckCircle2,
  Search
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "components/ui/select";
import { Input } from "components/ui/input";
import DatePicker from "components/generic/DatePicker";
import TimePicker from "components/crewManagement/TimePicker";
import {
  getCrewAvailability,
  AddAvailability,
  GetUpcomingShoots,
  getProjectDetails,
} from "services/creatorService";
import ProjectDetailsModal from "components/requirements/ProjectDetailsModal";
import { Calendar, MapPin, Clock, Video, Mic } from "lucide-react";
import { format } from "date-fns";
import toast from "react-hot-toast";

const formatLocation = (locationInput) => {
  if (!locationInput) return "Location TBD";
  let addressStr = locationInput;

  try {
    const parsed = JSON.parse(locationInput);
    if (parsed && parsed.address) addressStr = parsed.address;
  } catch (e) {
    /* Not JSON */
  }

  const parts = addressStr.split(",").map((p) => p.trim());
  if (parts.length >= 3) {
    const country = parts[parts.length - 1];
    const stateZip = parts[parts.length - 2];
    const city = parts[parts.length - 3];
    const state = stateZip.replace(/\d+/g, "").trim();
    return `${city}, ${state}, ${country}`;
  }
  return addressStr;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  // Returns format like: Dec 31, 2026
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatTimeDisplay = (timeString) => {
  if (!timeString) return "";
  // Handles HH:mm:ss or HH:mm
  const [hours, minutes] = timeString.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

export default function AvailabilityPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAllDay, setIsAllDay] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [upcomingShoots, setUpcomingShoots] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [projectDetails, setProjectDetails] = useState(null);
  const [projectLoading, setProjectLoading] = useState(false);

  const [formData, setFormData] = useState({
    type: "1",
    recurrence: "1", // ✅ default
    includeWeekends: false,
    repeatOn: [],
    monthlyDay: "",
    untilDate: null,
  });

  const [availability, setAvailability] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [summaryData, setSummaryData] = useState({
    availableDays: 0,
    bookedShoots: 0,
    timeOff: 0,
  });
  const [hoveredProject, setHoveredProject] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  // Fetch availability data from API
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const crewMemberId = user?.crew_member_id;

    if (!crewMemberId) return;

    getCrewAvailability({
      month: currentMonth,
      year: currentYear,
    }).then((response) => {
      if (response?.data?.availability) {
        setAvailability(response.data.availability);
      }
    });
  }, [currentMonth, currentYear]);

  useEffect(() => {
    GetUpcomingShoots({})
      .then((response) => {
        if (!response?.error && Array.isArray(response?.data)) {
          setUpcomingShoots(response.data);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch upcoming shoots", err);
      });
  }, []);

  const handleViewDetails = async (projectId) => {
    setIsSidebarOpen(true);
    setProjectLoading(true);

    try {
      const response = await getProjectDetails({
        project_id: projectId,
      });

      if (!response?.error) {
        setProjectDetails(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch project details", err);
    } finally {
      setProjectLoading(false);
    }
  };

  useEffect(() => {
    const getSummaryData = () => {
      let availableDays = 0;
      let bookedShoots = 0;
      let timeOff = 0;

      for (const dateKey in availability) {
        const availabilityStatus = availability[dateKey];
        if (availabilityStatus) {
          if (availabilityStatus.available) {
            availableDays += 1;
          }
          if (availabilityStatus.projectAssigned) {
            bookedShoots += 1;
          }
          if (
            !availabilityStatus.available &&
            !availabilityStatus.projectAssigned
          ) {
            timeOff += 1;
          }
        }
      }

      setSummaryData({ availableDays, bookedShoots, timeOff });
    };

    getSummaryData();
  }, [availability]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const user = JSON.parse(localStorage.getItem("user"));
    const crewMemberId = user?.crew_member_id;

    const formattedDate = format(new Date(selectedDate), "yyyy-MM-dd");

    // Build the initial Base Payload
    let payload = {
      crew_member_id: crewMemberId,
      date: formattedDate,
      availability_status: Number(formData.type),
      is_full_day: isAllDay ? 1 : 0,
      start_time: isAllDay ? null : formData.startTime,
      end_time: isAllDay ? null : formData.endTime,
      recurrence: Number(formData.recurrence),
      notes: formData.notes || "",
    };

    // Add Recurrence Specific Logic
    if (formData.recurrence !== "1") {
      // Add until date for all repeating types
      if (formData.untilDate) {
        payload.recurrence_until = format(
          new Date(formData.untilDate),
          "yyyy-MM-dd"
        );
      }

      // DAILY (Recurrence Type 2)
      if (formData.recurrence === "2") {
        if (!formData.includeWeekends) {
          payload.recurrence_days = ["mon", "tue", "wed", "thu", "fri"];
        }
      }
      // WEEKLY (Recurrence Type 3)
      else if (formData.recurrence === "3") {
        payload.recurrence_days = (formData.repeatOn || []).map((day) =>
          day.toLowerCase()
        );
      }
      // MONTHLY (Recurrence Type 4)
      else if (formData.recurrence === "4") {
        payload.recurrence_day_of_month = Number(formData.monthlyDay);
      }
    }

    try {
      await AddAvailability(payload);
      setIsModalOpen(false);

      // --- SUCCESS TOAST ---
      toast.custom(
        (t) => (
          <div
            className={`${t.visible ? "animate-enter" : "animate-leave"
              } max-w-md w-full bg-[#F0FDF4] dark:bg-green-950 border border-[#22C55E] dark:border-green-800 shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
          >
            <div className="flex-1 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-[#22C55E]" />
                  </div>
                </div>
                <div className="ml-3 flex-1 pr-6">
                  <p className="text-base font-bold text-gray-900 dark:text-white">
                    Availability Updated
                  </p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    Your schedule has been successfully updated.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        ),
        { position: "bottom-right" }
      );

      // Refresh data for the calendar
      const response = await getCrewAvailability({
        crew_member_id: crewMemberId,
        month: currentMonth,
        year: currentYear,
      });

      if (response?.data?.availability) {
        setAvailability(response.data.availability);
      }
    } catch (error) {
      console.error("Error adding availability:", error);

      // --- ERROR TOAST ---
      toast.custom(
        (t) => (
          <div
            className={`${t.visible ? "animate-enter" : "animate-leave"
              } max-w-md w-full bg-[#FEF2F2] dark:bg-red-950 border border-[#EF4444] dark:border-red-900 shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
          >
            <div className="flex-1 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
                  </div>
                </div>
                <div className="ml-3 flex-1 pr-6">
                  <p className="text-base font-bold text-gray-900 dark:text-white">
                    Update Failed
                  </p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    We couldn't save your availability. Please try again.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        ),
        { position: "bottom-right" }
      );
    }
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
    setIsAnimating(true);
  };

  const handleModalClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsModalOpen(false);
    }, 300); // match slideDown duration
  };

  const handleFormChange = (value, name) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value, // Update the form data with the selected value
    }));
  };

  const handleAllDayChange = () => {
    setIsAllDay(!isAllDay);
    if (!isAllDay) {
      setFormData({ ...formData, startTime: "", endTime: "" });
    }
  };

  const handleTimeChange = (time, field) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: time,
    }));
  };

  const handleMonthChange = (direction) => {
    if (direction === "prev") {
      setCurrentMonth((prevMonth) => (prevMonth === 1 ? 12 : prevMonth - 1));
      if (currentMonth === 1) {
        setCurrentYear(currentYear - 1);
      }
    } else if (direction === "next") {
      setCurrentMonth((prevMonth) => (prevMonth === 12 ? 1 : prevMonth + 1));
      if (currentMonth === 12) {
        setCurrentYear(currentYear + 1);
      }
    }
  };

  const handleDateLeave = () => {
    setHoveredProject(null);
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month - 1, 1).getDay();
  };

  const renderCalendarDays = () => {
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
    const calendarDays = [];

    for (let i = 0; i < firstDay; i++) {
      calendarDays.push(<div key={`empty-${i}`} className="h-28" />);
    }

    const today = new Date();
    const todayDateString = `${today.getFullYear()}-${today.getMonth() + 1 < 10
      ? "0" + (today.getMonth() + 1)
      : today.getMonth() + 1
      }-${today.getDate() < 10 ? "0" + today.getDate() : today.getDate()}`;

    for (let i = 1; i <= daysInMonth; i++) {
      const dateString = `${currentYear}-${currentMonth < 10 ? "0" + currentMonth : currentMonth
        }-${i < 10 ? "0" + i : i}`;
//     const todayDateString = format(today, "yyyy-MM-dd");

//     for (let i = 1; i <= daysInMonth; i++) {
//       const dateString = `${currentYear}-${
//         currentMonth < 10 ? "0" + currentMonth : currentMonth
//       }-${i < 10 ? "0" + i : i}`;

      const availabilityStatus = availability[dateString];
      const isAvailable = availabilityStatus?.available;
      const isAssigned = availabilityStatus?.projectAssigned;

      // Extract times: Priority 1: Project Times, Priority 2: Custom Status Times
      const displayStartTime = isAssigned
        ? availabilityStatus?.projectDetails?.start_time
        : availabilityStatus?.start_time;

      const displayEndTime = isAssigned
        ? availabilityStatus?.projectDetails?.end_time
        : availabilityStatus?.end_time;

      const hasSpecificTime = displayStartTime && displayEndTime;

      const isPastDate = dateString < todayDateString;
      const isToday = dateString === todayDateString;

      const cardBackground = isPastDate
        ? "bg-[#EFE6DA] dark:bg-neutral-800"
        : isAvailable
          ? "bg-[#FAF8F5] dark:bg-neutral-900/40"
          : isAssigned
            ? "bg-white dark:bg-neutral-900"
            : "bg-[#EFE6DA] dark:bg-neutral-800";

      const textColor = isAvailable
        ? "text-[#6F655B] dark:text-neutral-300"
        : "text-[#9C9185] dark:text-neutral-500";

      const handleDateClick = () => {
        if (!isPastDate) {
          setSelectedDate(dateString);
          handleModalOpen();
        }
      };

      const handleDateHover = (e) => {
        if (isAssigned && availabilityStatus?.projectDetails) {
          const rect = e.currentTarget.getBoundingClientRect();
          setHoverPosition({ x: rect.right + 10, y: rect.top });
          setHoveredProject({ date: dateString, ...availabilityStatus.projectDetails });
        }
      };

      calendarDays.push(
        <div
          key={i}
          onClick={handleDateClick}
          onMouseEnter={handleDateHover}
          onMouseLeave={handleDateLeave}
          className={`h-28 p-2 border border-[#E7DCCF] dark:border-neutral-800 text-xs transition-all duration-200
    ${cardBackground} ${textColor}
    ${!isPastDate ? "cursor-pointer" : ""}
    hover:bg-[#E3D0BC] dark:hover:bg-neutral-700`}
        >
          {/* DATE NUMBER (ONLY TODAY HIGHLIGHTED) */}
          {/* <div
            className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold mb-1 ${isToday
              ? "bg-[#594736] dark:bg-white text-white dark:text-black"
              : ""
              }`}
          >
            {i} */}
          <div className="flex justify-between items-start">
            <div
              className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold mb-1 ${
                isToday
                  ? "bg-[#594736] dark:bg-white text-white dark:text-black"
                  : ""
              }`}
            >
              {i}
            </div>

            {hasSpecificTime && (
              <div className="text-[9px] px-1 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 font-medium truncate max-w-[60px]">
                {formatTimeDisplay(displayStartTime)}
              </div>
            )}
          </div>

          {/* EVENTS */}
          {availabilityStatus && (
            <div className="space-y-1 mt-1">
              {isAssigned ? (
                <div className="space-y-1">
                  <EventDot
                    color="bg-blue-500"
                    label={
                      availabilityStatus.projectDetails?.project_name || "Shoot"
                    }
                  />
                  <div className="text-[9px] text-blue-600 dark:text-blue-400 font-bold">
                    {formatTimeDisplay(displayStartTime)} -{" "}
                    {formatTimeDisplay(displayEndTime)}
                  </div>
                </div>
              ) : (
                hasSpecificTime &&
                (isAvailable ? (
                  <div className="text-[9px] text-green-600 dark:text-green-400 font-medium">
                    Available: {formatTimeDisplay(displayStartTime)}-
                    {formatTimeDisplay(displayEndTime)}
                  </div>
                ) : (
                  <div className="text-[9px] text-red-500 italic">
                    UnAvailable: {formatTimeDisplay(displayStartTime)}-
                    {formatTimeDisplay(displayEndTime)}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      );
    }
    return calendarDays;
  };

  const RightSideBar = ({ }) => {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-blue-300 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/30 px-4 py-3 flex gap-3">
          <Info
            className="text-blue-600 dark:text-blue-400 mt-0.5"
            size={18}
          />
          <p className="text-sm text-blue-700 dark:text-blue-300 leading-snug">
            Your availability is automatically blocked for confirmed shoots
          </p>
        </div>

        {/* Color Legend */}
        <div className="rounded-xl border border-[#E7DCCF] dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
          <h3 className="font-semibold mb-3 text-sm">Color Legend</h3>

          <div className="space-y-3 text-sm">
            <Legend
              color="bg-[#cfbba1] dark:bg-neutral-700"
              label="Disabled"
              desc="Time off or blocked"
            />
            <Legend
              color="bg-[#7C7268] dark:bg-neutral-100"
              label="Today's"
              desc="Time off or blocked"
            />
            <Legend
              color="bg-blue-500"
              label="Shoots"
              desc="Confirmed shoots"
            />
            <Legend
              color="bg-purple-500"
              label="Equipment"
              desc="Equipment rentals"
            />
            <Legend
              color="bg-red-500"
              label="Conflicts"
              desc="Scheduling conflicts"
            />
          </div>
        </div>

        {/* Selected Dates */}
        <div className="rounded-xl border border-[#E7DCCF] dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
          <h3 className="font-semibold mb-3 text-sm">This Month</h3>

          <div className="space-y-2 text-sm">
            {/* Available Days */}
            <div className="flex justify-between items-center bg-[#F7F3EE] dark:bg-neutral-800 px-3 py-2 rounded-lg">
              <span className="text-[#6F655B] dark:text-neutral-400">
                Available Days
              </span>
              <span className="font-medium">{summaryData.availableDays}</span>
            </div>

            {/* Booked Shoots */}
            <div className="flex justify-between items-center bg-[#F7F3EE] dark:bg-neutral-800 px-3 py-2 rounded-lg">
              <span className="text-[#6F655B] dark:text-neutral-400">
                Booked Shoots
              </span>
              <span className="font-medium">{summaryData.bookedShoots}</span>
            </div>

            {/* Time Off */}
            <div className="flex justify-between items-center bg-[#F7F3EE] dark:bg-neutral-800 px-3 py-2 rounded-lg">
              <span className="text-[#6F655B] dark:text-neutral-400">
                Time Off
              </span>
              <span className="font-medium">{summaryData.timeOff} days</span>
            </div>

            {/* Equipment Rentals */}
            <div className="flex justify-between items-center bg-[#F7F3EE] dark:bg-neutral-800 px-3 py-2 rounded-lg">
              <span className="text-[#6F655B] dark:text-neutral-400">
                Equipment Rentals
              </span>
              <span className="font-medium">2</span>
            </div>
          </div>
        </div>

        {/* Share Availability */}
        <div className="rounded-xl border border-[#E7DCCF] dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
          <h3 className="font-semibold mb-3 text-sm">Share Availability</h3>

          <div className="space-y-2 text-sm">
            <p className="text-[#6F655B] dark:text-neutral-400">
              Share your availability link with production teams
            </p>
            <Button className="w-full bg-[#F7F3EE] dark:bg-neutral-800 text-[#6F655B] dark:text-neutral-300 border-[#E7DCCF] dark:border-neutral-700 text-sm hover:opacity-80">
              Copy Link
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-[1600px] mx-auto p-4 md:p-8 bg-white dark:bg-transparent text-gray-900 dark:text-white">
      <div className="flex flex-col md:flex-row items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-semibold">Manage Availability</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Set your available dates and times for upcoming projects
          </p>
        </div>
        <div className="flex justify-between items-center w-full md:w-fit mt-5 md:mt-0">
          <Button
            variant="outline"
            size="sm"
            className="md:hidden bg-[#FFFDF9] dark:bg-neutral-800 border-[#E7DCCF] dark:border-neutral-700"
          >
            All Events
          </Button>
          <Button
            onClick={handleModalOpen}
            className="bg-black dark:bg-white dark:text-black text-white rounded-lg px-4 md:w-64 hover:opacity-90"
          >
            + Add Availability
          </Button>
        </div>
      </div>

      {/* Calendar Section */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-9 space-y-6 order-2 lg:order-1">
          <div className="lg:rounded-2xl bg-white dark:bg-neutral-900 lg:border border-[#E7DCCF] dark:border-neutral-800 lg:p-4 shadow-sm overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 w-full">
              <div className="flex items-center gap-2 w-full">
                <button
                  onClick={() => handleMonthChange("prev")}
                  className="h-8 w-8 flex items-center justify-center rounded-lg border dark:border-neutral-700 bg-[#FFFDF9] dark:bg-neutral-800"
                >
                  <ChevronLeft size={16} />
                </button>

                <span className="text-sm font-medium mx-4">
                  {new Date(currentYear, currentMonth - 1).toLocaleString(
                    "default",
                    {
                      month: "long",
                      year: "numeric",
                    }
                  )}
                </span>

                <button
                  onClick={() => handleMonthChange("next")}
                  className="h-8 w-8 flex items-center justify-center rounded-lg border dark:border-neutral-700 bg-[#FFFDF9] dark:bg-neutral-800"
                >
                  <ChevronRight size={16} />
                </button>

                {/* <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto md:ml-0 bg-[#FFFDF9] dark:bg-neutral-800 border-[#E7DCCF] dark:border-neutral-700"
                >
                  Today
                </Button> */}
              </div>

              {/* <Button
                variant="outline"
                size="sm"
                className="hidden md:block bg-[#FFFDF9] dark:bg-neutral-800 border-[#E7DCCF] dark:border-neutral-700 mt-2 md:mt-0 shrink-0"
              >
                All Events
              </Button> */}
            </div>

            <div className="grid grid-cols-7 mt-1 rounded-xl overflow-hidden border border-[#E7DCCF] dark:border-neutral-800">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                (d, index) => (
                  <div
                    key={index}
                    className="py-2 text-center text-xs font-medium text-[#6F655B] dark:text-neutral-400 bg-[#F1EEEA] dark:bg-neutral-800 border border-[#E7DCCF] dark:border-neutral-700"
                  >
                    {d}
                  </div>
                )
              )}
              {renderCalendarDays()}
            </div>
          </div>

          <div className="md:hidden">
            <RightSideBar />
          </div>

          {/* ================= UPCOMING SHOOTS CARD ================= */}
          <div className="rounded-2xl bg-[#F9F4ED] dark:bg-neutral-900/50 border border-[#E7DCCF] dark:border-neutral-800 p-5">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base lg:text-lg font-semibold">Upcoming Shoots</h2>
              <button className="text-sm md:text-base font-semibold text-[#A59479] dark:text-neutral-400 hover:underline">
                View All
              </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
  {/* Search Container */}
  <div className="relative w-64">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9C9185]" />
    <Input
      placeholder="Search events or crew..."
      // Added h-10 to match others
      className="pl-9 h-10 bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 focus-visible:ring-1"
    />
  </div>

  {/* Filters Group */}
  <div className="flex flex-wrap items-center gap-3">
    <Select>
      {/* Added h-10 and unified border color */}
      <SelectTrigger className="h-10 w-40 bg-white dark:bg-neutral-900 text-sm border-neutral-200 dark:text-white dark:border-neutral-700">
        <SelectValue placeholder="By Category" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="shoot">Shoot</SelectItem>
        <SelectItem value="equipment">Equipment</SelectItem>
      </SelectContent>
    </Select>

    <Select>
      {/* Added h-10 and unified border color */}
      <SelectTrigger className="h-10 w-32 bg-white dark:bg-neutral-900 text-sm border-neutral-200 dark:text-white dark:border-neutral-700">
        <SelectValue placeholder="Status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="upcoming">Upcoming</SelectItem>
        <SelectItem value="completed">Completed</SelectItem>
      </SelectContent>
    </Select>

    <Button
      variant="outline"
      // Removed size="sm" and added h-10 to match inputs exactly
      className="h-10 px-4 bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-sm font-normal"
    >
      Today
    </Button>
  </div>
</div>

            {/* Shoot Cards */}
            <div className="space-y-4">
              {upcomingShoots.length === 0 && (
                <p className="text-sm text-[#9C9185] dark:text-neutral-500">
                  No upcoming shoots
                </p>
              )}

              {upcomingShoots.map((shoot) => (
                <UpcomingShootCard
                  key={shoot.project_id}
                  data={shoot}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          </div>
          <ProjectDetailsModal
            open={isSidebarOpen}
            project={projectDetails}
            onOpenChange={(open) => {
              setIsSidebarOpen(open);
              if (!open) setProjectDetails(null);
            }}
          />
        </div>
        {/* Right Sidebar */}
        <div className="hidden lg:block lg:order-2 lg:col-span-3 ">
          <RightSideBar />
        </div>
      </div>

      {/* Modal Form */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm">
          <div
            className={`w-full md:max-w-lg bg-white dark:bg-neutral-900 text-gray-900 dark:text-white rounded-t-[20px] md:rounded-2xl p-6 relative
      ${isAnimating ? "animate-slideUp" : "animate-slideDown"}
      max-h-[90vh] overflow-y-auto border dark:border-neutral-800 shadow-2xl transition-all duration-300`}
          >
            <button
              onClick={handleModalClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-black dark:hover:text-white transition"
            >
              <X size={18} />
            </button>

            <h2 className="text-xl font-semibold mb-1">Add Availability</h2>
            <p className="text-sm text-gray-500 mb-4">
              Set your availability, time off, or block time for shoots.
            </p>

            <form onSubmit={handleSubmit}>
              {/* TYPE */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Type</label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => handleFormChange(value, "type")}
                >
                  <SelectTrigger className="w-full text-sm dark:bg-neutral-800 dark:border-neutral-700">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full ${formData.type === "1" ? "bg-green-500" : "bg-red-500"
                          }`}
                      />
                      <SelectValue placeholder="Select option" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="dark:bg-neutral-900 dark:border-neutral-700">
                    <SelectItem value="1">Available</SelectItem>
                    <SelectItem value="2">Not Available</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* DATE */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Date</label>
                <DatePicker
                  id="date"
                  disabled={formData.recurrence === "4"}
                  onChange={(d) => {
                    setSelectedDate(d);
                    if (formData.recurrence === "4") {
                      handleFormChange(d?.getDate().toString(), "monthlyDay");
                    }
                  }}
                />
              </div>

              {/* TIME */}
              {!isAllDay && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium">
                      Start Time
                    </label>
                    <div className="w-full bg-white dark:bg-background border dark:border-neutral-700 rounded-lg px-4 mt-2">
                      <TimePicker
                        setTime={(time) => handleTimeChange(time, "startTime")}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium">
                      End Time
                    </label>
                    <div className="w-full bg-white dark:bg-background border dark:border-neutral-700 rounded-lg px-4 mt-2">
                      <TimePicker
                        setTime={(time) => handleTimeChange(time, "endTime")}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ALL DAY */}
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="allDay"
                  checked={isAllDay}
                  onChange={handleAllDayChange}
                  className="rounded border-gray-300 dark:bg-neutral-800"
                />
                <label
                  htmlFor="allDay"
                  className="text-sm font-medium cursor-pointer"
                >
                  All day
                </label>
              </div>

              {/* RECURRENCE SELECTION */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Recurrence
                </label>
                <Select
                  value={formData.recurrence}
                  onValueChange={(value) =>
                    handleFormChange(value, "recurrence")
                  }
                >
                  <SelectTrigger className="w-full text-sm dark:bg-neutral-800 dark:border-neutral-700">
                    <SelectValue placeholder="Does not repeat" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-neutral-900 dark:border-neutral-700">
                    <SelectItem value="1">Does Not Repeat</SelectItem>
                    <SelectItem value="2">Daily</SelectItem>
                    <SelectItem value="3">Weekly</SelectItem>
                    <SelectItem value="4">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* CONDITIONAL RECURRENCE FIELDS */}
              {formData.recurrence !== "1" && (
                <div className="space-y-4 mb-6 p-4 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border dark:border-neutral-800">
                  {/* DAILY */}
                  {formData.recurrence === "2" && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="incWeekends"
                        checked={formData.includeWeekends}
                        onChange={(e) =>
                          handleFormChange(e.target.checked, "includeWeekends")
                        }
                        className="rounded border-gray-300"
                      />
                      <label
                        htmlFor="incWeekends"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Include Weekends
                      </label>
                    </div>
                  )}

                  {/* WEEKLY */}
                  {formData.recurrence === "3" && (
                    <div className="space-y-2">
                      <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Repeat on
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                          (day) => {
                            const isSelected = (
                              formData.repeatOn || []
                            ).includes(day);
                            return (
                              <button
                                key={day}
                                type="button"
                                onClick={() => {
                                  const current = formData.repeatOn || [];
                                  const updated = current.includes(day)
                                    ? current.filter((d) => d !== day)
                                    : [...current, day];
                                  handleFormChange(updated, "repeatOn");
                                }}
                                className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-all duration-200 ${isSelected
                                  ? "bg-[#0B1D33] border-[#0B1D33] text-white dark:bg-blue-600 dark:border-blue-600 dark:text-white"
                                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-400 dark:bg-neutral-800 dark:border-neutral-700 dark:text-gray-300 dark:hover:border-neutral-500"
                                  }`}
                              >
                                {day}
                              </button>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )}

                  {/* MONTHLY */}
                  {/* MONTHLY */}
                  {formData.recurrence === "4" && (
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <span>Repeat on Day</span>
                      <input
                        type="text"
                        value={formData.monthlyDay ?? ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, "");
                          if (val === "" || (Number(val) >= 1 && Number(val) <= 31)) {
                            handleFormChange(val, "monthlyDay");
                          }
                        }}
                        // Added text-gray-900 and dark:text-white below
                        className="w-14 px-2 py-1 rounded-md bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 text-gray-900 dark:text-white text-center outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <span>of each month</span>
                    </div>
                  )}

                  {/* UNTIL DATE */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Until Date
                    </label>
                    <DatePicker
                      id="untilDate"
                      onChange={(d) => handleFormChange(d, "untilDate")}
                    />
                  </div>

                  {/* INFO BOX */}
                  {formData.untilDate && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 p-3 rounded-lg text-blue-600 dark:text-blue-400 text-xs font-medium">
                      {formData.recurrence === "2" &&
                        `Repeats ${formData.includeWeekends
                          ? "every day"
                          : "every weekday"
                        } until ${format(
                          new Date(formData.untilDate),
                          "MMM dd, yyyy"
                        )}.`}
                      {formData.recurrence === "3" &&
                        `Repeats on ${(formData.repeatOn || []).join(", ") || "..."
                        } until ${format(
                          new Date(formData.untilDate),
                          "MMM dd, yyyy"
                        )}.`}
                      {formData.recurrence === "4" &&
                        `Repeats on day ${formData.monthlyDay || "..."
                        } until ${format(
                          new Date(formData.untilDate),
                          "MMM dd, yyyy"
                        )}.`}
                    </div>
                  )}
                </div>
              )}

              {/* ACTIONS */}
              <div className="flex md:justify-end gap-3 mt-6">
                <Button
                  type="button"
                  className="border border-gray-200 hover:text-foreground dark:border-neutral-700 px-6 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-neutral-800 transition w-full md:w-fit"
                  onClick={handleModalClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#0B1D33] text-white px-8 py-2 rounded-xl hover:bg-black transition"
                >
                  Save Availability
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {hoveredProject && (
        <div
          className="fixed z-50 w-[420px] bg-white dark:bg-neutral-900 rounded-2xl border border-[#E8DDD3] dark:border-neutral-800 shadow-xl"
          style={{
            top: hoverPosition.y,
            left: hoverPosition.x,
          }}
          onMouseLeave={handleDateLeave}
        >
          {/* Header */}
          <div className="p-5 border-b border-[#EFE6DA] dark:border-neutral-800">
            <span className="inline-block mb-2 px-3 py-1 text-xs rounded-full bg-[#2F6D5A] text-white">
              Active
            </span>

            <h3 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">
              {hoveredProject.project_name}
            </h3>

            <div className="flex flex-wrap gap-4 text-xs text-[#8F8F8F] dark:text-neutral-400 mt-2">
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {hoveredProject.date}
              </span>

              <span className="flex items-center gap-1">
                <MapPin size={14} />
                {formatLocation(hoveredProject.event_location)}
              </span>

              <span className="flex items-center gap-1">
                <Clock size={14} />
              </span>
            </div>
          </div>

          {/* Middle */}
          <div className="grid grid-cols-2 divide-x dark:divide-neutral-800 border-b border-[#EFE6DA] dark:border-neutral-800">
            {/* Streaming */}
            <div className="p-4">
              <p className="text-xs text-[#9C9185] dark:text-neutral-500 mb-2">
                Streaming Platforms
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="flex items-center gap-1 px-2 py-1 text-xs border dark:border-neutral-700 rounded-lg">
                  <Video size={14} /> YouTube
                </span>
                <span className="flex items-center gap-1 px-2 py-1 text-xs border dark:border-neutral-700 rounded-lg">
                  <Mic size={14} /> Twitch
                </span>
                <span className="px-2 py-1 text-xs border dark:border-neutral-700 rounded-lg">
                  +4
                </span>
              </div>
            </div>

            {/* Equipment */}
            <div className="p-4">
              <p className="text-xs text-[#9C9185] dark:text-neutral-500 mb-2">
                Equipment Assigned
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="flex items-center gap-1 px-2 py-1 text-xs border dark:border-neutral-700 rounded-lg">
                  📷 Camera
                </span>
                <span className="flex items-center gap-1 px-2 py-1 text-xs border dark:border-neutral-700 rounded-lg">
                  🎥 Video
                </span>
                <span className="px-2 py-1 text-xs border dark:border-neutral-700 rounded-lg">
                  +3
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 flex items-center justify-between">
            <span className="text-xs text-[#9C9185] dark:text-neutral-500">
              Updated 4h ago
            </span>

            {/* <button
              onClick={() => {
                handleViewDetails(hoveredProject.project_id);
                setHoveredProject(null);
              }}
              className="px-4 py-2 rounded-lg bg-black dark:bg-white text-white dark:text-black text-xs hover:opacity-80 transition-all"
            >
              View Details
            </button> */}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper Functions
function Legend({ color, label, desc }) {
  return (
    <div className="flex items-start gap-2">
      <span className={`mt-1 w-2.5 h-2.5 rounded-full ${color}`} />
      <div>
        <p className="font-medium leading-none text-gray-900 dark:text-white">
          {label}
        </p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
    </div>
  );
}

function EventDot({ color, label }) {
  return (
    <div className="flex items-center gap-1 text-[#6F655B] dark:text-neutral-400">
      <span className={`h-2 w-2 rounded-full ${color}`} />
      <span className="truncate text-xs">{label}</span>
    </div>
  );
}

function UpcomingShootCard({ data, hideButton, onViewDetails }) {
  const formattedDate = data?.event_date
    ? new Date(data.event_date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    : "Date TBD";

  const timeLabel =
    data?.start_time && data?.end_time
      ? `${data.start_time} – ${data.end_time}`
      : "Time TBD";

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-xl border border-[#E7DCCF] p-4 flex items-start justify-between gap-4 shadow-sm">
      {/* Left Content */}
      <div className="space-y-2">
        <h3 className="font-semibold text-sm text-gray-900 dark:text-white">
          {data?.project_name || "Untitled Project"}
        </h3>

        <div className="flex flex-wrap gap-4 text-xs text-[#6F655B] dark:text-neutral-400">
          <span className="flex items-center gap-1">
            <Calendar size={16} />
            {formattedDate}
          </span>

          <span className="flex items-center gap-1">
            <MapPin size={16} />
            {formatLocation(data?.event_location)}
          </span>

          <span className="flex items-center gap-1">
            <Clock size={16} />
            {timeLabel}
          </span>
        </div>
        <p className="text-xs text-[#9C9185] dark:text-neutral-500 mt-2">
          {data?.is_completed ? "Completed Shoot" : "Upcoming Shoot"}
        </p>
      </div>

      {/* Right Content */}
      <div className="flex flex-col items-end gap-3">
        {/* Crew Avatars */}
        <div className="flex -space-x-2">
          {[1, 2, 3].map((i) => (
            <img
              key={i}
              src={`https://i.pravatar.cc/40?img=${i}`}
              alt="crew"
              className="h-7 w-7 rounded-full border-2 border-white dark:border-neutral-800"
            />
          ))}
          <span className="h-7 w-7 rounded-full bg-[#F1EEEA] dark:bg-neutral-800 text-xs flex items-center justify-center border border-[#E7DCCF] dark:border-neutral-700 text-gray-600 dark:text-neutral-400">
            +3
          </span>
        </div>

        {!hideButton && (
          <Button
            onClick={() => onViewDetails(data.project_id)}
            className="bg-black dark:bg-white text-white dark:text-black text-xs px-4 py-2 rounded-lg hover:opacity-80"
          >
            View Details
          </Button>
        )}
      </div>
    </div>
  );
}
